import { goalPlan } from "../../engine/calculations";

/**
 * Factory for savings-backed goal calculators (Mission Financial Azadi goal family: Child
 * Education / Wedding / Dream Home / Car / Vacation / Custom Goal). They all share one calculation
 * — inflate today's cost to the goal date, subtract what current savings grow to, size the monthly
 * SIP for the shortfall — so they're one factory + a config each, not six copies (CLAUDE.md
 * "adding a calculator = writing a config"). `loan: true` adds a loan-percentage input for goals
 * you finance (home/car), so you only save for the down payment.
 *
 * Field keys are deliberately `goalName` / `presentCost` / `yearsToGoal` so the profile saved-goal
 * picker (CalculatorRenderer.handlePickSavedGoal) can auto-fill any config built here.
 */
export function makeGoalCalculator({
  key,
  title,
  description,
  loan = false,
  goalNameField = false,
  goalPicker = false,
  labels = {},
  defaults = {},
}) {
  const {
    presentCost: presentCostLabel = "Cost today",
    years: yearsLabel = "Years until the goal",
    currentSavings: currentSavingsLabel = "Current savings for this goal",
    loan: loanLabel = "Funded by loan",
  } = labels;

  const fields = [];
  if (goalNameField) {
    fields.push({ key: "goalName", label: "Name your goal", type: "text", default: defaults.goalName || "My goal" });
  }
  fields.push(
    { key: "presentCost", label: presentCostLabel, type: "currency", default: defaults.presentCost ?? 20_00_000, min: 1000, max: 50_00_00_000, help: "What it would cost if you paid for it today." },
    { key: "yearsToGoal", label: yearsLabel, type: "number", default: defaults.yearsToGoal ?? 10, min: 1, max: 40 }
  );
  if (loan) {
    fields.push({
      key: "loanPct",
      label: loanLabel,
      type: "percent",
      default: defaults.loanPct ?? 80,
      min: 0,
      max: 95,
      help: "Share funded by a loan — you only save for the rest (the down payment).",
    });
  }
  fields.push(
    { key: "currentSavings", label: currentSavingsLabel, type: "currency", default: defaults.currentSavings ?? 0, min: 0, max: 50_00_00_000, help: "What you've already set aside for this goal (grows at your expected return)." },
    { key: "inflation", label: "Inflation", type: "percent", default: 6, min: 0, max: 15, assumptionKey: "inflation" },
    { key: "returnRate", label: "Expected annual return", type: "percent", default: 12, min: 1, max: 30, assumptionKey: "equityReturn" }
  );

  return {
    key,
    title,
    category: "Life Goals",
    description,
    goalPicker,
    fields,
    chart: {
      type: "bar",
      xKey: "name",
      series: [{ key: "amount", label: "Amount", colorVar: "--gold" }],
    },
    compute(values) {
      const presentCost = Number(values.presentCost) || 0;
      const years = Number(values.yearsToGoal) || 0;
      const currentSavings = Number(values.currentSavings) || 0;
      const inflation = (Number(values.inflation) || 0) / 100;
      const annualReturn = (Number(values.returnRate) || 0) / 100;
      const fundedFraction = loan ? (Number(values.loanPct) || 0) / 100 : 0;

      const plan = goalPlan({ presentCost, inflation, years, currentSavings, annualReturn, fundedFraction });
      const goalLabel = goalNameField && values.goalName ? `"${values.goalName}"` : "this goal";

      const extraHeadlines = [{ label: "Future cost", value: plan.futureCost }];
      extraHeadlines.push(
        loan
          ? { label: "Down payment you fund", value: plan.selfFunded }
          : { label: "Current savings grow to", value: plan.existingFV }
      );

      const breakdown = [
        { label: "Years left", value: years, format: "text" },
        { label: "Future cost of the goal", value: plan.futureCost },
      ];
      if (loan) {
        breakdown.push({ label: "Loan-funded", value: plan.loanFunded });
        breakdown.push({ label: "Down payment (you fund)", value: plan.selfFunded });
      }
      breakdown.push({ label: "Current savings grow to", value: plan.existingFV });
      breakdown.push({ label: "Still to fund", value: plan.shortfall });
      breakdown.push({ label: "Or invest a lumpsum today", value: plan.requiredLumpsum });

      const series = [
        { name: loan ? "Down payment" : "Future cost", amount: loan ? plan.selfFunded : plan.futureCost },
        { name: "Current savings", amount: plan.existingFV },
        { name: "To fund via SIP", amount: plan.shortfall },
      ];

      return {
        headline: { label: `Monthly SIP for ${goalLabel}`, value: plan.monthlySIP },
        extraHeadlines,
        breakdown,
        series,
      };
    },
  };
}
